/**
 * The page's clocks, as pure arithmetic. Three clocks meet here: the server's
 * monotonic milliseconds (every Path entry's `due`), the page's own monotonic
 * milliseconds (`performance.now`, running from page load), and the audio
 * context's seconds (`currentTime`, running only once a click has resumed it).
 * ADR 0005: the page keeps time on its own clock and pairs it to audio at cue
 * time. Nothing here reads a clock; every function is given its numbers.
 */

/** Every browser plays this far behind the server, so that a Cue always lands in the future. */
export const PLAYBACK_LEAD_MS = 200

/** About one audio quantum at 44.1 or 48 kHz. A smaller shift in the mapping is not worth a re-cue. */
export const QUANTUM_MS = 3

/** How many pong samples the shortest-round-trip choice looks back over. */
export const OFFSET_SAMPLES = 8

/** How far back a pairing of the two local clocks is still trusted. */
export const PAIRING_WINDOW_MS = 3000

/** One pong: server ms minus page ms, and how long the exchange took. */
export interface OffsetSample {
  readonly offset: number
  readonly roundTrip: number
}

/** offset = s − (a_sent + a_recv) / 2: the server's clock read at the midpoint of the round trip. */
export const offsetSample = (pong: {
  readonly aSent: number
  readonly aRecv: number
  readonly s: number
}): OffsetSample => ({
  offset: pong.s - (pong.aSent + pong.aRecv) / 2,
  roundTrip: pong.aRecv - pong.aSent
})

/**
 * The offset in use: the sample with the shortest round trip among the last
 * `keep`. A long round trip means the midpoint could be anywhere in it, so it
 * loses; a stall heals itself once its pongs fall out of the window.
 */
export const bestOffset = (
  samples: ReadonlyArray<OffsetSample>,
  keep: number = OFFSET_SAMPLES
): number | undefined => {
  let best: OffsetSample | undefined
  for (const sample of samples.slice(-keep)) {
    if (best === undefined || sample.roundTrip < best.roundTrip) best = sample
  }
  return best?.offset
}

/** `currentTime` and `performance.now()` read back to back, in that order. */
export interface Pairing {
  readonly audio: number
  readonly page: number
}

/**
 * The pairing in use: among the pairs read in the last `windowMs`, the one with
 * the smallest page − audio. A read of `currentTime` is stale by up to one
 * render callback and stale only ever means behind, so the smallest difference
 * is the freshest read, the same trick as the shortest round trip.
 */
export const bestPairing = (
  pairs: ReadonlyArray<Pairing>,
  now: number,
  windowMs: number = PAIRING_WINDOW_MS
): Pairing | undefined => {
  let best: Pairing | undefined
  for (const pair of pairs) {
    if (pair.page < now - windowMs) continue
    if (best === undefined || pair.page - pair.audio * 1000 < best.page - best.audio * 1000) best = pair
  }
  return best
}

/** When a Path entry lands, on the page clock: its server due, translated, plus the playback lead. */
export const localDue = (due: number, offset: number): number => due - offset + PLAYBACK_LEAD_MS

/** The audio time of a page time, through a pairing. */
export const audioTimeOf = (pageMs: number, pairing: Pairing): number =>
  pairing.audio + (pageMs - pairing.page) / 1000

/** The page time of an audio time, through a pairing. */
export const pageTimeOf = (audio: number, pairing: Pairing): number =>
  pairing.page + (audio - pairing.audio) * 1000

/** The absolute audio frame a local due falls on. */
export const cueFrame = (localDueMs: number, pairing: Pairing, sampleRate: number): number =>
  Math.round(audioTimeOf(localDueMs, pairing) * sampleRate)

/**
 * Everything the page needs to place a due time: the offset always, the
 * pairing only once a click has resumed the context. Behind the splash the
 * pairing is undefined and the mapping ends on the page clock, where the
 * Onlooker's timer dot lives.
 */
export interface Mapping {
  readonly offset: number
  readonly pairing: Pairing | undefined
}

/**
 * How far `next` moves the landing of any due, in ms, compared with `current`.
 * Both mappings are affine with slope one, so the shift is one constant. Gaining
 * or losing the pairing is not a shift but a change of clock: infinite.
 */
export const mappingShiftMs = (current: Mapping, next: Mapping): number => {
  const byOffset = current.offset - next.offset
  if (current.pairing === undefined && next.pairing === undefined) return byOffset
  if (current.pairing === undefined || next.pairing === undefined) return Infinity
  const byPairing =
    (next.pairing.audio - current.pairing.audio) * 1000 - (next.pairing.page - current.pairing.page)
  return byOffset + byPairing
}

/**
 * The hysteresis: a new estimate is adopted only when it moves the mapping by
 * more than about one quantum. Adopting one is what re-cues every pending Step,
 * so a correction sounds as one gap changing by the delta, once.
 */
export const adopt = (
  current: Mapping | undefined,
  next: Mapping,
  thresholdMs: number = QUANTUM_MS
): Mapping =>
  current === undefined || Math.abs(mappingShiftMs(current, next)) > thresholdMs ? next : current
